import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  Snackbar,
  TextField,
} from "@mui/material";
import apiService from "../../services/api";
import FileUploadInput from "../../components/forms/FileUploadInput";
import CustomLoading from "../../components/common/CustomLoading";
import useCodeCommonByCategory from "../../hooks/useCodeCommonByCategory";
import { PictureAsPdf } from "@mui/icons-material";

interface SettingsFormData {
  privacyPolicy: string;
  termsAndConditions: string;
  rentalAgreement: string;
}

type SystemSettingRow = {
  systemsettingsid?: number;
  systemsettingskey?: string;
  systemsettingsvalue?: string;
};

function parseSystemSettingsList(response: unknown): SystemSettingRow[] {
  if (Array.isArray(response)) return response as SystemSettingRow[];
  if (response != null && typeof response === "object") {
    const data = (response as { data?: unknown }).data;
    if (Array.isArray(data)) return data as SystemSettingRow[];
  }
  return [];
}

function findRefundDaysSetting(list: SystemSettingRow[]): {
  value: string;
  systemsettingsid: number | null;
} {
  for (const row of list) {
    const key = String(row.systemsettingskey ?? "")
      .trim()
      .toLowerCase();
    if (key === "refunddays" || key === "refund_days") {
      const id = Number(row.systemsettingsid);
      return {
        value: String(row.systemsettingsvalue ?? "").trim(),
        systemsettingsid: Number.isFinite(id) && id > 0 ? id : null,
      };
    }
  }
  return { value: "", systemsettingsid: null };
}

const SettingsPage: React.FC = () => {
  const [formData, setFormData] = useState<SettingsFormData>({
    privacyPolicy: "",
    termsAndConditions: "",
    rentalAgreement: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<Record<string, boolean>>({
    privacyPolicy: false,
    termsAndConditions: false,
    rentalAgreement: false,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "success" });
  const [fetchingAttachments, setFetchingAttachments] = useState(false);
  const [fetchingRefundDays, setFetchingRefundDays] = useState(false);
  const [refundDays, setRefundDays] = useState("");
  const [refundDaysSettingId, setRefundDaysSettingId] = useState<number | null>(
    null,
  );
  const [refundDaysError, setRefundDaysError] = useState("");
  const hasFetchedAttachments = useRef(false);

  // Fetch RegulatoryDocumentType codes
  const { statusCodes, loading: codesLoading } = useCodeCommonByCategory(
    "RegulatoryDocumentType",
  );

  // Map document types to form fields
  // This maps form field names to document type codevalues
  const documentTypeMap = useMemo(() => {
    const map: Record<string, number | null> = {
      privacyPolicy: null,
      termsAndConditions: null,
      rentalAgreement: null,
    };

    // Find codevalues for each document type
    // Match based on display name patterns
    for (const code of statusCodes) {
      const displayName = code.codedisplayname.toLowerCase().trim();
      const codeValue = Number.parseInt(code.codevalue, 10);

      // Privacy Policy mapping
      if (displayName.includes("privacy")) {
        map.privacyPolicy = codeValue;
      }
      // Terms & Conditions mapping
      else if (
        displayName.includes("terms") ||
        displayName.includes("condition")
      ) {
        map.termsAndConditions = codeValue;
      }
      // Rental Agreement mapping
      else if (
        displayName.includes("rental") ||
        (displayName.includes("agreement") && !displayName.includes("terms"))
      ) {
        map.rentalAgreement = codeValue;
      }
    }

    // If no codes found, use default mapping: 1=Privacy, 2=Terms, 3=Rental
    if (statusCodes.length === 0) {
      map.privacyPolicy = 1;
      map.termsAndConditions = 2;
      map.rentalAgreement = 3;
    }

    return map;
  }, [statusCodes]);

  // Fetch existing attachments function
  const fetchAttachments = useCallback(
    async (forceRefetch: boolean = false) => {
      // Wait for statusCodes to load
      if (codesLoading || statusCodes.length === 0) {
        return;
      }

      // Wait for documentTypeMap to have values
      if (
        !documentTypeMap.privacyPolicy &&
        !documentTypeMap.termsAndConditions &&
        !documentTypeMap.rentalAgreement
      ) {
        return;
      }

      // Prevent multiple calls unless forced
      if (!forceRefetch && hasFetchedAttachments.current) {
        return;
      }

      hasFetchedAttachments.current = true;
      setFetchingAttachments(true);

      try {
        // Get documenttype codevalues from the map and convert to integers
        const privacyPolicyType = documentTypeMap.privacyPolicy
          ? Number.parseInt(String(documentTypeMap.privacyPolicy), 10)
          : null;
        const termsAndConditionsType = documentTypeMap.termsAndConditions
          ? Number.parseInt(String(documentTypeMap.termsAndConditions), 10)
          : null;
        const rentalAgreementType = documentTypeMap.rentalAgreement
          ? Number.parseInt(String(documentTypeMap.rentalAgreement), 10)
          : null;

        // Build array of documenttypes with their field mappings
        const documentTypeMappings = [
          { documenttype: privacyPolicyType, field: "privacyPolicy" as const },
          {
            documenttype: termsAndConditionsType,
            field: "termsAndConditions" as const,
          },
          {
            documenttype: rentalAgreementType,
            field: "rentalAgreement" as const,
          },
        ].filter((item) => item.documenttype !== null);

        // Fetch for each documenttype (only 3 calls)
        const promises = documentTypeMappings.map(
          async ({ documenttype, field }) => {
            try {
              const response = await apiService.getPrivacyTermsTemplate(
                documenttype!,
              );
              const data = (response as any)?.data;
              if (data?.agreementpath) {
                return {
                  field,
                  documenttype: documenttype!,
                  agreementname: data.agreementname || "",
                  agreementpath: data.agreementpath || "",
                };
              }
              return null;
            } catch {
              // Silently fail for individual requests
              return null;
            }
          },
        );

        const results = await Promise.all(promises);

        // Map results to form fields
        for (const result of results) {
          if (result) {
            setFormData((prev) => ({
              ...prev,
              [result.field]: result.agreementpath,
            }));
          }
        }
      } catch (error: any) {
        console.error("Error fetching attachments:", error);
        // Don't show error to user as this is a background fetch
      } finally {
        setFetchingAttachments(false);
      }
    },
    [
      codesLoading,
      statusCodes.length,
      documentTypeMap.privacyPolicy,
      documentTypeMap.termsAndConditions,
      documentTypeMap.rentalAgreement,
    ],
  );

  // Fetch existing attachments on component mount (only once)
  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const fetchRefundDaysSetting = useCallback(async () => {
    setFetchingRefundDays(true);
    try {
      const res = await apiService.getSystemSettingsData();
      const list = parseSystemSettingsList(res);
      const { value, systemsettingsid } = findRefundDaysSetting(list);
      setRefundDays(value);
      setRefundDaysSettingId(systemsettingsid);
      setRefundDaysError("");
    } catch (error: unknown) {
      console.error("Error fetching refund days setting:", error);
      setSnackbar({
        open: true,
        message: "Failed to load refund days setting.",
        severity: "error",
      });
    } finally {
      setFetchingRefundDays(false);
    }
  }, []);

  useEffect(() => {
    void fetchRefundDaysSetting();
  }, [fetchRefundDaysSetting]);

  const validateRefundDays = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      setRefundDaysError("Refund days is required.");
      return null;
    }
    const n = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n < 0 || String(n) !== trimmed) {
      setRefundDaysError("Enter a whole number 0 or greater.");
      return null;
    }
    setRefundDaysError("");
    return n;
  };

  const handleFileUpload = async (field: string, file: File) => {
    setUploading((prev) => ({ ...prev, [field]: true }));
    try {
      // Validate file type - only PDF files allowed
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");

      if (!isPdf) {
        throw new Error(
          "Only PDF files are allowed. Please upload a PDF document.",
        );
      }

      const response = await apiService.uploadImage(
        file,
        "regulatory",
        "regulatory",
      );
      let fileUrl = file.name;
      if (response?.data?.savedFiles && response.data.savedFiles.length > 0) {
        const savedFile = response.data.savedFiles[0];
        fileUrl = savedFile.filePath || savedFile.fileName || file.name;
      } else if (response?.data) {
        fileUrl =
          response.data.url ||
          response.data.filePath ||
          response.data.fileName ||
          response.data.imageUrl ||
          file.name;
      }
      setFormData((prev) => ({ ...prev, [field]: fileUrl }));
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to upload file. Please try again.",
        severity: "error",
      });
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const refundDaysValue = validateRefundDays(refundDays);
      if (refundDaysValue === null) {
        setLoading(false);
        return;
      }

      // Check if document types are loaded
      if (codesLoading || statusCodes.length === 0) {
        setSnackbar({
          open: true,
          message: "Document types are still loading. Please wait...",
          severity: "warning",
        });
        setLoading(false);
        return;
      }

      // Prepare payload array
      const payload: Array<{
        agreementname: string;
        agreementpath: string;
        documenttype: number;
      }> = [];

      const missingTypes: string[] = [];

      // Add Privacy Policy if uploaded
      if (formData.privacyPolicy) {
        if (documentTypeMap.privacyPolicy !== null) {
          payload.push({
            agreementname: "Privacy Policy",
            agreementpath: formData.privacyPolicy,
            documenttype: documentTypeMap.privacyPolicy,
          });
        } else {
          missingTypes.push("Privacy Policy");
        }
      }

      // Add Terms & Conditions if uploaded
      if (formData.termsAndConditions) {
        if (documentTypeMap.termsAndConditions !== null) {
          payload.push({
            agreementname: "Terms & Conditions",
            agreementpath: formData.termsAndConditions,
            documenttype: documentTypeMap.termsAndConditions,
          });
        } else {
          missingTypes.push("Terms & Conditions");
        }
      }

      // Add Rental Agreement if uploaded
      if (formData.rentalAgreement) {
        if (documentTypeMap.rentalAgreement !== null) {
          payload.push({
            agreementname: "Rental Agreement",
            agreementpath: formData.rentalAgreement,
            documenttype: documentTypeMap.rentalAgreement,
          });
        } else {
          missingTypes.push("Rental Agreement");
        }
      }

      if (payload.length > 0) {
        if (missingTypes.length > 0) {
          setSnackbar({
            open: true,
            message: `Warning: Could not find document types for: ${missingTypes.join(", ")}. Saving only available documents.`,
            severity: "warning",
          });
        }
        await apiService.saveTermsPrivacyAndAgreements(payload);
      }
      await fetchRefundDaysSetting();
      if (payload.length > 0) {
        await fetchAttachments(true);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to save settings. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (filePath: string): string => {
    if (!filePath) return "";
    if (filePath.startsWith("http")) return filePath;

    const BASE_IMAGE_URL = process.env.REACT_APP_API_BASE_URL;
    let filename = filePath.replace(/^\/+/, "");
    if (filename.includes("/")) {
      const parts = filename.split("/");
      filename = parts.at(-1) || filename;
    }
    filename = filename.replace(/^\/+/, "");
    return `${BASE_IMAGE_URL}/CommonService/Images/Regulatory_Documents/${filename}`;
  };

  const isPdfFile = (filePath: string): boolean => {
    return filePath.toLowerCase().endsWith(".pdf");
  };

  return (
    <>
      <Container fluid className="px-0">
        {/* Upload Documents Section */}
        <Paper className="mb-4 p-4 border-0 rounded-0 shadow-none">
          <Typography
            variant="h6"
            className="mb-4 fw-bold"
            sx={{ fontSize: "1.25rem", color: "rgba(22, 67, 135, 1)" }}
          >
            Upload Documents
          </Typography>
          <Row className="g-3">
            <Col xs={12} md={4}>
              <FileUploadInput
                field="privacyPolicy"
                label="Upload Privacy Policy"
                formData={formData}
                onChange={handleChange}
                folderPath="settings"
                folderType="document"
                onImageUpload={handleFileUpload}
                uploading={uploading.privacyPolicy}
                accept="application/pdf,.pdf"
              />
            </Col>
            <Col xs={12} md={4}>
              <FileUploadInput
                field="termsAndConditions"
                label="Upload Terms & Conditions"
                formData={formData}
                onChange={handleChange}
                folderPath="settings"
                folderType="document"
                onImageUpload={handleFileUpload}
                uploading={uploading.termsAndConditions}
                accept="application/pdf,.pdf"
              />
            </Col>
            <Col xs={12} md={4}>
              <FileUploadInput
                field="rentalAgreement"
                label="Upload Rental Agreement"
                formData={formData}
                onChange={handleChange}
                folderPath="settings"
                folderType="document"
                onImageUpload={handleFileUpload}
                uploading={uploading.rentalAgreement}
                accept="application/pdf,.pdf"
              />
            </Col>
          </Row>
        </Paper>

        {/* Attachments Section */}
        <Paper className="mb-4 p-4 border-0 rounded-0 shadow-none">
          <Typography
            variant="h6"
            className="mb-4 fw-bold"
            sx={{ fontSize: "1.25rem", color: "#333" }}
          >
            Attachments
          </Typography>
          {fetchingAttachments ? (
            <CustomLoading message="Loading attachments..." />
          ) : (
            <>
              {formData.privacyPolicy ||
              formData.termsAndConditions ||
              formData.rentalAgreement ? (
                <Row className="g-3">
                  {formData.privacyPolicy && (
                    <Col xs={12} md={4}>
                      <Box
                        sx={{
                          border: "1px solid #dee2e6",
                          borderRadius: 1,
                          p: 2,
                          textAlign: "center",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        {isPdfFile(formData.privacyPolicy) ? (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "100px",
                                  height: "100px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: 2,
                                  border: "2px solid #e0e0e0",
                                }}
                              >
                                <PictureAsPdf
                                  sx={{
                                    fontSize: "64px",
                                    color: "#d32f2f",
                                  }}
                                />
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#666",
                                display: "block",
                                textAlign: "center",
                                mb: 1,
                              }}
                            >
                              Privacy Policy
                            </Typography>
                            <Box
                              sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                href={getFileUrl(formData.privacyPolicy)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <img
                            src={getFileUrl(formData.privacyPolicy)}
                            alt="Privacy Policy"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              objectFit: "contain",
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        )}
                      </Box>
                    </Col>
                  )}
                  {formData.termsAndConditions && (
                    <Col xs={12} md={4}>
                      <Box
                        sx={{
                          border: "1px solid #dee2e6",
                          borderRadius: 1,
                          p: 2,
                          textAlign: "center",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        {isPdfFile(formData.termsAndConditions) ? (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "100px",
                                  height: "100px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: 2,
                                  border: "2px solid #e0e0e0",
                                }}
                              >
                                <PictureAsPdf
                                  sx={{
                                    fontSize: "64px",
                                    color: "#d32f2f",
                                  }}
                                />
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#666",
                                display: "block",
                                textAlign: "center",
                                mb: 1,
                              }}
                            >
                              Terms & Conditions
                            </Typography>
                            <Box
                              sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                href={getFileUrl(formData.termsAndConditions)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <img
                            src={getFileUrl(formData.termsAndConditions)}
                            alt="Terms & Conditions"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              objectFit: "contain",
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        )}
                      </Box>
                    </Col>
                  )}
                  {formData.rentalAgreement && (
                    <Col xs={12} md={4}>
                      <Box
                        sx={{
                          border: "1px solid #dee2e6",
                          borderRadius: 1,
                          p: 2,
                          textAlign: "center",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        {isPdfFile(formData.rentalAgreement) ? (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "100px",
                                  height: "100px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: 2,
                                  border: "2px solid #e0e0e0",
                                }}
                              >
                                <PictureAsPdf
                                  sx={{
                                    fontSize: "64px",
                                    color: "#d32f2f",
                                  }}
                                />
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#666",
                                display: "block",
                                textAlign: "center",
                                mb: 1,
                              }}
                            >
                              Rental Agreement
                            </Typography>
                            <Box
                              sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                href={getFileUrl(formData.rentalAgreement)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <img
                            src={getFileUrl(formData.rentalAgreement)}
                            alt="Rental Agreement"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              objectFit: "contain",
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        )}
                      </Box>
                    </Col>
                  )}
                </Row>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "#666", textAlign: "center", py: 2 }}
                >
                  No attachments available
                </Typography>
              )}
            </>
          )}
        </Paper>
        {/* <Paper className="mb-4 p-4 border-0 rounded-0 shadow-none">
          <Typography
            variant="h6"
            className="mb-3 fw-bold"
            sx={{ fontSize: "1.25rem", color: "rgba(22, 67, 135, 1)" }}
          >
            Refund settings
          </Typography>
          <Row className="g-3">
            <Col xs={12} md={4}>
              <TextField
                label="Refund days"
                type="number"
                size="small"
                fullWidth
                value={refundDays}
                onChange={(e) => {
                  setRefundDays(e.target.value);
                  if (refundDaysError) validateRefundDays(e.target.value);
                }}
                onBlur={() => validateRefundDays(refundDays)}
                disabled={loading || fetchingRefundDays}
                error={Boolean(refundDaysError)}
                helperText={
                  refundDaysError ||
                  "Number of days allowed to process a refund after ride completion."
                }
                inputProps={{ min: 0, step: 1 }}
              />
            </Col>
          </Row>
        </Paper> */}
        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 4,
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || fetchingRefundDays}
            sx={{
              backgroundColor: "rgba(22, 67, 135, 1)",
              minWidth: "150px",
              "&:hover": {
                backgroundColor: "rgba(22, 67, 135, 0.9)",
              },
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SettingsPage;
